import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import {
  Reply as ReplyIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PriorityHigh as PriorityHighIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import api from '../../utils/axios';

interface SupportRequest {
  id: string;
  subject: string;
  description: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  category: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  contactEmail?: string;
  phoneNumber?: string;
  preferredContact: 'email' | 'phone' | 'portal';
  response?: string;
  attachments: string[];
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Feedback {
  id: string;
  type: 'bug' | 'feature' | 'improvement' | 'praise' | 'other';
  rating: number;
  title: string;
  description: string;
  category: string;
  contactEmail?: string;
  priority?: 'low' | 'medium' | 'high';
  userType?: 'new' | 'regular' | 'power';
  featureRequest?: string;
  improvementArea?: string;
  status: 'SUBMITTED' | 'REVIEWED' | 'IMPLEMENTED' | 'REJECTED';
  response?: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

const SupportManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [supportRequests, setSupportRequests] = useState<SupportRequest[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<SupportRequest | Feedback | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 0) {
        const response = await api.get('/support', {
          params: { status: statusFilter || undefined, priority: priorityFilter || undefined }
        });
        if (response.data.success) {
          setSupportRequests(response.data.data);
        }
      } else {
        const response = await api.get('/feedback', {
          params: { status: statusFilter || undefined }
        });
        if (response.data.success) {
          setFeedback(response.data.data);
        }
      }
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (itemId: string, newStatus: string, type: 'support' | 'feedback') => {
    try {
      const endpoint = type === 'support' 
        ? `/support/${itemId}/status`
        : `/feedback/${itemId}/status`;
      
      await api.patch(endpoint, { status: newStatus });
      fetchData();
    } catch (err) {
      setError('Failed to update status');
      console.error('Error updating status:', err);
    }
  };

  const handleResponse = async () => {
    if (!selectedItem) return;
    
    try {
      const endpoint = activeTab === 0 
        ? `/support/${selectedItem.id}/status`
        : `/feedback/${selectedItem.id}/status`;
      
      await api.patch(endpoint, { 
        response: responseText,
        status: activeTab === 0 ? 'RESOLVED' : 'REVIEWED'
      });
      
      setDialogOpen(false);
      setResponseText('');
      setSelectedItem(null);
      fetchData();
    } catch (err) {
      setError('Failed to send response');
      console.error('Error sending response:', err);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
      case 'high':
        return 'error';
      case 'HIGH':
      case 'medium':
        return 'warning';
      case 'MEDIUM':
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN':
      case 'SUBMITTED':
        return 'warning';
      case 'IN_PROGRESS':
      case 'REVIEWED':
        return 'info';
      case 'RESOLVED':
      case 'IMPLEMENTED':
        return 'success';
      case 'CLOSED':
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderSupportRequests = () => (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="OPEN">Open</MenuItem>
            <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
            <MenuItem value="RESOLVED">Resolved</MenuItem>
            <MenuItem value="CLOSED">Closed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            label="Priority"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="LOW">Low</MenuItem>
            <MenuItem value="MEDIUM">Medium</MenuItem>
            <MenuItem value="HIGH">High</MenuItem>
            <MenuItem value="URGENT">Urgent</MenuItem>
          </Select>
        </FormControl>
        <Button onClick={fetchData} variant="outlined">Filter</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Subject</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {supportRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>{request.subject}</TableCell>
                <TableCell>
                  {request.user.firstName} {request.user.lastName}
                  <br />
                  <Typography variant="caption" color="textSecondary">
                    {request.user.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={request.priority}
                    color={getPriorityColor(request.priority) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={request.status}
                    color={getStatusColor(request.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{request.category}</TableCell>
                <TableCell>
                  {dayjs(request.createdAt).format('MMM DD, YYYY')}
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedItem(request);
                        setDialogOpen(true);
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reply">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedItem(request);
                        setResponseText(request.response || '');
                        setDialogOpen(true);
                      }}
                    >
                      <ReplyIcon />
                    </IconButton>
                  </Tooltip>
                  {request.status === 'OPEN' && (
                    <Tooltip title="Mark In Progress">
                      <IconButton
                        size="small"
                        onClick={() => handleStatusUpdate(request.id, 'IN_PROGRESS', 'support')}
                      >
                        <PriorityHighIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {request.status === 'IN_PROGRESS' && (
                    <Tooltip title="Mark Resolved">
                      <IconButton
                        size="small"
                        onClick={() => handleStatusUpdate(request.id, 'RESOLVED', 'support')}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderFeedback = () => (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="SUBMITTED">Submitted</MenuItem>
            <MenuItem value="REVIEWED">Reviewed</MenuItem>
            <MenuItem value="IMPLEMENTED">Implemented</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
          </Select>
        </FormControl>
        <Button onClick={fetchData} variant="outlined">Filter</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedback.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.title}</TableCell>
                <TableCell>
                  {item.user.firstName} {item.user.lastName}
                  <br />
                  <Typography variant="caption" color="textSecondary">
                    {item.user.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={item.type} size="small" />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {item.rating}/5
                    <Box sx={{ ml: 1 }}>
                      {[...Array(5)].map((_, i) => (
                        <span key={i} style={{ color: i < item.rating ? '#ffc107' : '#e0e0e0' }}>
                          ★
                        </span>
                      ))}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.status}
                    color={getStatusColor(item.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {dayjs(item.createdAt).format('MMM DD, YYYY')}
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedItem(item);
                        setDialogOpen(true);
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reply">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedItem(item);
                        setResponseText(item.response || '');
                        setDialogOpen(true);
                      }}
                    >
                      <ReplyIcon />
                    </IconButton>
                  </Tooltip>
                  {item.status === 'SUBMITTED' && (
                    <Tooltip title="Mark Reviewed">
                      <IconButton
                        size="small"
                        onClick={() => handleStatusUpdate(item.id, 'REVIEWED', 'feedback')}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Support & Feedback Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label={`Support Requests (${supportRequests.length})`} />
          <Tab label={`Feedback (${feedback.length})`} />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {activeTab === 0 ? renderSupportRequests() : renderFeedback()}
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedItem(null);
          setResponseText('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {activeTab === 0 ? 'Support Request Details' : 'Feedback Details'}
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    {activeTab === 0 
                      ? (selectedItem as SupportRequest).subject
                      : (selectedItem as Feedback).title
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {activeTab === 0 
                      ? (selectedItem as SupportRequest).description
                      : (selectedItem as Feedback).description
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">User:</Typography>
                  <Typography variant="body2">
                    {selectedItem.user.firstName} {selectedItem.user.lastName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedItem.user.email}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Status:</Typography>
                  <Chip
                    label={selectedItem.status}
                    color={getStatusColor(selectedItem.status) as any}
                    size="small"
                  />
                </Grid>
                {activeTab === 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">Priority:</Typography>
                      <Chip
                        label={(selectedItem as SupportRequest).priority}
                        color={getPriorityColor((selectedItem as SupportRequest).priority) as any}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">Category:</Typography>
                      <Typography variant="body2">
                        {(selectedItem as SupportRequest).category}
                      </Typography>
                    </Grid>
                  </>
                )}
                {activeTab === 1 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">Type:</Typography>
                      <Chip
                        label={(selectedItem as Feedback).type}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">Rating:</Typography>
                      <Typography variant="body2">
                        {(selectedItem as Feedback).rating}/5
                      </Typography>
                    </Grid>
                  </>
                )}
                {selectedItem.response && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Previous Response:</Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {selectedItem.response}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Response"
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Enter your response..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setSelectedItem(null);
              setResponseText('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResponse}
            variant="contained"
            disabled={!responseText.trim()}
          >
            Send Response
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportManagement; 